import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock, Calculator, PackageSearch } from "lucide-react";
import api from "../../lib/api";
import { flagFor } from "../../lib/countryFlags";
import CountrySearchSelect from "../CountrySearchSelect";

const TABS = [
  { id: "track", label: "Track Shipment", icon: PackageSearch, testid: "action-tab-track" },
  { id: "quote", label: "Get a Quote", icon: Calculator, testid: "action-tab-quote" },
  { id: "pickup", label: "Book Pickup", icon: CalendarClock, testid: "action-tab-pickup" },
];

const inputCls = "w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 text-sm font-medium text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-saffron";

export default function ActionPanel() {
  const [tab, setTab] = useState("track");
  const [awb, setAwb] = useState("");
  const [countries, setCountries] = useState([]);
  const [quote, setQuote] = useState({ from: "", to: "", weight: "", type: "Parcel" });
  const [pickup, setPickup] = useState({ location: "", destination: "", date: "", phone: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rates/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  return (
    <section id="quick-actions" className="relative z-20 mx-auto -mt-20 max-w-5xl px-4 sm:px-6" data-testid="quick-action-panel">
      <div className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-2xl shadow-stone-300/60">
        <div className="flex border-b border-stone-200" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)} data-testid={t.testid}
                    className={`flex flex-1 items-center justify-center gap-2 px-3 py-4 text-xs font-bold uppercase tracking-wider transition-colors sm:text-sm ${tab === t.id ? "bg-saffron text-white" : "text-slate-500 hover:bg-stone-50 hover:text-navy"}`}>
              <t.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        <div className="p-5 sm:p-7">
          {tab === "track" && (
            <form onSubmit={(e) => { e.preventDefault(); if (awb.trim()) navigate(`/track/${encodeURIComponent(awb.trim().toUpperCase())}`); }}
                  className="flex flex-col gap-3 sm:flex-row" data-testid="action-track-form">
              <input className={`${inputCls} flex-1`} placeholder="Enter AWB / Tracking Number"
                     value={awb} onChange={(e) => setAwb(e.target.value)} data-testid="action-track-awb-input" />
              <button type="submit" data-testid="action-track-awb-button"
                      className="group flex items-center justify-center gap-2 rounded-xl bg-saffron px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
                Track Shipment <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}

          {tab === "quote" && (
            <form onSubmit={(e) => { e.preventDefault(); navigate(`/rate-calculator?mode=international&country=${encodeURIComponent(quote.to)}&weight=${encodeURIComponent(quote.weight)}`); }}
                  className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="action-quote-form">
              <input className={inputCls} placeholder="From (pincode)" value={quote.from}
                     onChange={(e) => setQuote({ ...quote, from: e.target.value })} data-testid="action-quote-from" />
              <CountrySearchSelect countries={countries} value={quote.to} inputClass={inputCls}
                                   onChange={(v) => setQuote({ ...quote, to: v })}
                                   placeholder="To (country) — type to search" testid="action-quote-to" />
              <input className={inputCls} placeholder="Weight (kg)" type="number" step="0.1" min="0.1" required value={quote.weight}
                     onChange={(e) => setQuote({ ...quote, weight: e.target.value })} data-testid="action-quote-weight" />
              <select className={inputCls} value={quote.type}
                      onChange={(e) => setQuote({ ...quote, type: e.target.value })} data-testid="action-quote-type">
                {["Document", "Parcel", "Commercial"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <button type="submit" data-testid="action-quote-submit"
                      className="group flex items-center justify-center gap-2 rounded-xl bg-saffron px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
                Calculate Shipping <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}

          {tab === "pickup" && (
            <form onSubmit={(e) => { e.preventDefault(); navigate(`/pickup-request?phone=${encodeURIComponent(pickup.phone)}&pincode=${encodeURIComponent(pickup.location)}&date=${encodeURIComponent(pickup.date)}&destination=${encodeURIComponent(pickup.destination)}`); }}
                  className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="action-pickup-form">
              <input className={inputCls} placeholder="Pickup pincode" value={pickup.location}
                     onChange={(e) => setPickup({ ...pickup, location: e.target.value })} data-testid="action-pickup-location" />
              <input className={inputCls} placeholder="Destination (city/country)" value={pickup.destination}
                     onChange={(e) => setPickup({ ...pickup, destination: e.target.value })} data-testid="action-pickup-destination" />
              <input className={inputCls} type="date" value={pickup.date}
                     onChange={(e) => setPickup({ ...pickup, date: e.target.value })} data-testid="action-pickup-date" />
              <input className={inputCls} placeholder="Contact number" value={pickup.phone}
                     onChange={(e) => setPickup({ ...pickup, phone: e.target.value })} data-testid="action-pickup-phone" />
              <button type="submit" data-testid="action-pickup-submit"
                      className="group flex items-center justify-center gap-2 rounded-xl bg-saffron px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
                Schedule Pickup <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
